import { useQuery } from '@tanstack/react-query';
import { draftOrderClient, type CartView, type DraftStartContext } from '@/lib/draft-order-client';

/** Matches the chat's own 5s cadence, so the cart and the messages never look out of step. */
const ACTIVE_INTERVAL_MS = 5000;
/**
 * The idle cadence: no cart is believed to exist and the panel is closed, so this poll only guards
 * against the conversation-list's `hasActiveOrder` flag being stale (the selected conversation is a
 * snapshot). One cheap indexed read every 30s instead of the full 5s cadence.
 */
const IDLE_INTERVAL_MS = 30000;

interface UseConversationDraftOptions {
  conversationId?: string;
  /**
   * Pause polling. The panel sets this while it holds unsaved edits: a refetch that landed mid-edit
   * would either clobber the operator's working copy or make the "unsaved changes" banner lie about
   * what it is comparing against.
   */
  paused?: boolean;
  /**
   * Something suggests a live cart (the conversation's `hasActiveOrder` flag, or the panel is open):
   * poll at the chat's cadence. Otherwise drop to a slow safety-net tick.
   */
  active?: boolean;
  /**
   * Ask the server what starting a cart would take (suggested branch, recent-order warning). Only
   * the open start panel needs it — it costs the server extra lookups, so the background poll that
   * merely keeps the header button honest must not send it.
   */
  includeStartContext?: boolean;
}

interface UseConversationDraftResult {
  draft: CartView | null;
  /** Present only when `includeStartContext` was set and there is no cart. */
  startContext?: DraftStartContext;
  isLoading: boolean;
  refetch: () => Promise<unknown>;
}

/**
 * The conversation's live cart, or null when there is none.
 *
 * Deliberately a plain polled query rather than a new SSE channel: the draft is a DB row the agent
 * writes through the same API, and the inbox already refreshes on this cadence.
 */
export function useConversationDraft({
  conversationId,
  paused = false,
  active = true,
  includeStartContext = false,
}: UseConversationDraftOptions): UseConversationDraftResult {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['conversation-draft', conversationId, includeStartContext],
    queryFn: () => draftOrderClient.getForConversation(conversationId as string, { includeStartContext }),
    enabled: Boolean(conversationId),
    // On error React Query keeps the last data, so a transient failure does not make the cart
    // button vanish mid-edit.
    refetchInterval: paused ? false : active ? ACTIVE_INTERVAL_MS : IDLE_INTERVAL_MS,
    // Opening the panel flips `includeStartContext` and with it the query key; carrying the same
    // conversation's data across that flip keeps the live cart from flashing into the start panel
    // for a frame. Another conversation's data is never carried — its cart is not this one's.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === conversationId ? previousData : undefined,
  });

  return { draft: data?.cart ?? null, startContext: data?.startContext, isLoading, refetch };
}
